import { useMemo, useState } from "react";
import { useGameStore } from "@/store/gameStore";
import { useLegalActions } from "@/hooks/useLegalActions";
import type { EngineAction } from "@/engine/GameEngine";
import { findSubstituteConfig } from "@/engine/GameEngine";
import type { DeckCard, DamageType, ResponseKind } from "@/types/cards";
import Card from "./Card";

type RespondAction = Extract<EngineAction, { type: "respond" }>;

const DAMAGE_LABEL: Record<DamageType, string> = {
  w: "物理",
  hw: "混乱物理",
  f: "法术",
  sf: "神圣法术",
  ss: "损失生命",
  mana: "汲取魔法",
};

export default function ResponsePrompt() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  const dispatch = useGameStore((s) => s.dispatch);
  const legalActions = useLegalActions();
  const [subMode, setSubMode] = useState(false);
  const [subPicks, setSubPicks] = useState<number[]>([]);

  const visible = useMemo(() => {
    if (!state || state.phase !== "response") return false;
    const w = state.responseWindow;
    if (!w) return false;
    if (!w.responders.includes(humanId)) return false;
    return w.decisions[humanId] === "pending";
  }, [state, humanId]);

  const respondActions = useMemo<RespondAction[]>(
    () =>
      legalActions.filter((a): a is RespondAction => a.type === "respond"),
    [legalActions]
  );

  if (!state || !visible) return null;
  const w = state.responseWindow!;
  const effect = w.effect;

  // 来源 / 牌 / 技能 / 目标
  const names = new Map(state.players.map((p) => [p.id, p.hero.name]));
  const sourceName = names.get(effect.source) ?? effect.source;
  const targetId = Array.isArray(effect.target) ? effect.target[0] : effect.target;
  const targetName = names.get(targetId) ?? targetId;
  const isTarget = targetId === humanId;
  const isTeammate = !isTarget
    ? state.players.find((p) => p.id === targetId)?.team ===
      state.players.find((p) => p.id === humanId)?.team
    : false;

  // 预计伤害/效果
  const damageType = effect.damageType;
  const damageAmount = effect.damageAmount ?? 0;

  // 响应候选牌
  const human = state.players.find((p) => p.id === humanId)!;
  const realRespondActions = respondActions.filter((a) => a.cardUid !== -1);
  const substituteRespondActions = respondActions.filter((a) => a.cardUid === -1);
  const respondCards: DeckCard[] = realRespondActions
    .map((a) => human.hand.find((c) => c.uid === a.cardUid))
    .filter((c): c is DeckCard => Boolean(c));

  // 去重：同一张牌可能有多个 responseKind（理论上不会，但稳健处理）
  const uniqueRespondCards = Array.from(
    new Map(respondCards.map((c) => [c.uid, c])).values()
  );

  function pickKind(card: DeckCard) {
    const a = realRespondActions.find((x) => x.cardUid === card.uid);
    if (!a) return;
    dispatch({
      type: "respond",
      playerId: humanId,
      cardUid: card.uid,
      responseKind: a.responseKind as ResponseKind,
    });
  }

  function pass() {
    dispatch({ type: "pass-response", playerId: humanId });
  }

  // 替换响应：选2张手牌后 dispatch
  function toggleSubPick(uid: number) {
    setSubPicks((prev) =>
      prev.includes(uid)
        ? prev.filter((x) => x !== uid)
        : prev.length < 2
          ? [...prev, uid]
          : [prev[1], uid]
    );
  }

  function confirmSubstitute() {
    if (subPicks.length !== 2) return;
    const a = substituteRespondActions[0];
    if (!a) return;
    dispatch({
      type: "respond",
      playerId: humanId,
      cardUid: -1,
      substituteCardUids: [subPicks[0], subPicks[1]],
      responseKind: a.responseKind,
    });
    setSubMode(false);
    setSubPicks([]);
  }

  return (
    <div className="response-prompt" role="dialog" aria-modal="true">
      <div className="response-prompt__banner">
        <div className="response-prompt__title">响应窗口</div>
        <div className="response-prompt__info">
          <span className="response-prompt__source">{sourceName}</span>
          {effect.card && (
            <span className="response-prompt__card">
              【{effect.card.name}】
            </span>
          )}
          {effect.skill && (
            <span className="response-prompt__skill">
              技能 {effect.skill.name}
            </span>
          )}
          <span className="response-prompt__arrow">→</span>
          <span className="response-prompt__target">{targetName}</span>
          {damageType && (
            <span className="response-prompt__damage">
              预计 {damageAmount} {DAMAGE_LABEL[damageType]}
            </span>
          )}
          {effect.healHp ? (
            <span className="response-prompt__heal">
              恢复 {effect.healHp} HP
            </span>
          ) : null}
          {effect.healMp ? (
            <span className="response-prompt__heal">
              恢复 {effect.healMp} MP
            </span>
          ) : null}
          {effect.op && (
            <span className="response-prompt__op">
              操作 {effect.op.kind}
            </span>
          )}
          {isTeammate && (
            <span className="response-prompt__teammate">队友受击</span>
          )}
        </div>
      </div>
      {!subMode ? (
        <>
          <div className="response-prompt__cards">
            {uniqueRespondCards.length === 0 ? (
              <span className="response-prompt__empty">无可响应牌</span>
            ) : (
              uniqueRespondCards.map((c) => (
                <Card
                  key={c.uid}
                  card={c}
                  compact
                  highlight
                  onClick={() => pickKind(c)}
                />
              ))
            )}
          </div>
          <div className="response-prompt__actions">
            {substituteRespondActions.length > 0 && (
              <button
                className="response-prompt__btn response-prompt__btn--sub"
                onClick={() => setSubMode(true)}
              >
                替换响应：用2张手牌当{substituteRespondActions[0].responseKind}
              </button>
            )}
            <button
              className="response-prompt__btn response-prompt__btn--pass"
              onClick={pass}
            >
              跳过
            </button>
          </div>
        </>
      ) : (
        <div className="response-prompt__sub">
          <div className="response-prompt__sub-hint">
            选择2张手牌作为{substituteRespondActions[0]?.responseKind}打出
            （已选 {subPicks.length}/2）
          </div>
          <div className="response-prompt__cards">
            {human.hand.map((c) => {
              // 狂风刃甲：仅黑色可选
              const sub = findSubstituteConfig(human, "respond");
              const suitBlocked = !!(sub?.config.suitFilter && c.suit !== sub.config.suitFilter);
              return (
                <Card
                  key={c.uid}
                  card={c}
                  compact
                  highlight={subPicks.includes(c.uid)}
                  selected={subPicks.includes(c.uid)}
                  disabled={suitBlocked}
                  onClick={() => !suitBlocked && toggleSubPick(c.uid)}
                />
              );
            })}
          </div>
          <div className="response-prompt__actions">
            <button
              className="response-prompt__btn"
              disabled={subPicks.length !== 2}
              onClick={confirmSubstitute}
            >
              确认
            </button>
            <button
              className="response-prompt__btn response-prompt__btn--pass"
              onClick={() => { setSubMode(false); setSubPicks([]); }}
            >
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// 非阻塞提示条：响应窗口开启但当前等待的是其他玩家（如自己喝药水后，
// 敌方队伍依次决定是否使用【拒援】）。此时人类手牌全部置灰，必须明确提示
// "正在等待谁、可能发生什么"，避免误以为卡牌卡死/不可用。
export function ResponseWaitingBanner() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  if (!state || state.phase !== "response") return null;
  const w = state.responseWindow;
  if (!w) return null;

  // 轮到人类自己决策时由 ResponsePrompt 弹窗处理
  if (w.responders.includes(humanId) && w.decisions[humanId] === "pending") {
    return null;
  }
  const pending = w.responders.filter((rid) => w.decisions[rid] === "pending");
  if (pending.length === 0) return null;

  const names = pending.map(
    (rid) => state.players.find((p) => p.id === rid)?.hero.name ?? rid
  );
  const effect = w.effect;
  const cardName = effect.card?.name ?? effect.skill?.name ?? null;
  const mine = effect.source === humanId;

  // 该窗口允许的响应类型 → 玩家能预期"可能被什么挡下"
  let kindHint = "";
  if (effect.allowResponses.includes("拒援")) {
    kindHint = "对方可使用【拒援】抵消";
  } else if (effect.allowResponses.length > 0) {
    kindHint = `可能被【${effect.allowResponses.join(" / ")}】响应`;
  }

  return (
    <div className="waiting-banner" role="status">
      <span className="waiting-banner__dot" />
      <span>
        {mine ? "你" : ""}
        {cardName ? `使用【${cardName}】后，` : ""}
        等待【{names.join("】【")}】响应…
        {kindHint ? `（${kindHint}）` : ""}
        每位约 1.5 秒，结算前手牌暂不可操作
      </span>
    </div>
  );
}
